/**
 * Pre-React boot splash lifecycle — index.html owner until cold-boot transition completes.
 * Auth + public share routes release immediately after React bootstrap (no admin-gate/data owners).
 */

import { isPreReactEarlyReleasePath } from '@/lib/authPublicPaths';

const AUTH_PREREACT_FALLBACK_MS = 600;

let coldBootSessionActive = false;
let authFallbackTimer: ReturnType<typeof setTimeout> | null = null;

function currentPathname(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

function markPreReactBootStarted(): void {
  const w = window as Window & {
    __preReactBootVisible?: boolean;
    __preReactBootStartedAt?: number;
  };
  w.__preReactBootVisible = true;
  w.__preReactBootStartedAt = Date.now();
}

function clearAuthFallbackTimer(): void {
  if (authFallbackTimer !== null) {
    window.clearTimeout(authFallbackTimer);
    authFallbackTimer = null;
  }
}

function scheduleEarlyReleasePreReactFallback(): void {
  clearAuthFallbackTimer();
  authFallbackTimer = window.setTimeout(() => {
    authFallbackTimer = null;
    if (isPreReactEarlyReleasePath(currentPathname())) {
      releasePreReactBootSplash('early-release-fallback-timeout');
    }
  }, AUTH_PREREACT_FALLBACK_MS);
}

export function initPreReactBootHandoff(): void {
  if (typeof document === 'undefined') return;
  const el = document.getElementById('pre-react-boot-splash');
  if (!el) return;

  markPreReactBootStarted();

  if (isPreReactEarlyReleasePath(currentPathname())) {
    coldBootSessionActive = false;
    scheduleEarlyReleasePreReactFallback();
    return;
  }

  coldBootSessionActive = true;
}

export function isColdBootSessionActive(): boolean {
  return coldBootSessionActive;
}

export function endColdBootSession(): void {
  coldBootSessionActive = false;
}

export function isPreReactBootVisible(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.getElementById('pre-react-boot-splash');
  return !!el && el.isConnected;
}

export function releasePreReactBootSplash(_reason?: string): void {
  if (typeof document === 'undefined') return;
  clearAuthFallbackTimer();
  const el = document.getElementById('pre-react-boot-splash');
  if (!el) return;
  el.remove();
  const w = window as Window & { __preReactBootVisible?: boolean };
  w.__preReactBootVisible = false;
  endColdBootSession();
}

/**
 * Auth / public-share early-release routes: React/bootstrap ready — release preReact overlay.
 * Name retained for call-site compatibility; guard uses `isPreReactEarlyReleasePath`.
 */
export function releasePreReactBootSplashForAuthRoute(reason = 'auth-route-ready'): void {
  if (!isPreReactEarlyReleasePath(currentPathname())) return;
  releasePreReactBootSplash(reason);
}

/** Admin cold-boot: release when coordinator no longer needs to suppress content surfaces. */
export function maybeReleasePreReactBootSplash(coldBootTransitionActive: boolean): void {
  if (!coldBootTransitionActive && isPreReactBootVisible()) {
    releasePreReactBootSplash('cold-boot-complete');
  }
}

/** Test-only reset */
export function resetColdBootSessionForTests(): void {
  coldBootSessionActive = false;
  clearAuthFallbackTimer();
}

export function activateColdBootSessionForTests(): void {
  coldBootSessionActive = true;
}
